import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type {
  IClaimAddressValue,
  IClaimConditionalDetailValue,
  IClaimInfoCardProps,
} from '@/@types/claims';
import { Building2, CreditCard, FileText, Heart, ShieldCheck, User } from 'lucide-react';
import { formatDate, formatMoney } from './claimDetailUtils';

function isAddressValue(v: unknown): v is IClaimAddressValue {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isConditionalDetailValue(v: unknown): v is IClaimConditionalDetailValue {
  return (
    typeof v === 'object' &&
    v !== null &&
    !Array.isArray(v) &&
    'answer' in v &&
    typeof (v as Record<string, unknown>)['answer'] === 'boolean' // AS_JUSTIFIED: narrowed by 'answer' in v check above
  );
}

function formatAddress(v: unknown): string {
  if (!isAddressValue(v)) return '—';
  const parts = [v.street, v.city, v.postalCode, v.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

function formatConditionalDetail(v: unknown): string {
  if (!isConditionalDetailValue(v)) return '—';
  const base = v.answer ? 'Yes' : 'No';
  return v.details ? `${base} — ${v.details}` : base;
}

function str(v: unknown): string {
  if (v === undefined || v === null || v === '') return '—';
  return String(v);
}

function InfoField({ label, value }: { label: string; value: string }) {
  // LOCAL_TYPE_JUSTIFIED: tiny display helper, props not reused outside this file
  return (
    <div>
      <dt className="text-foreground-muted text-[11px]">{label}</dt>
      <dd className="font-medium mt-0.5 text-[13px] text-foreground">{value}</dd>
    </div>
  );
}

function SectionGrid({ children }: { children: React.ReactNode }) {
  // LOCAL_TYPE_JUSTIFIED: layout wrapper only used in this file
  return <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">{children}</dl>;
}

const ALL_SECTIONS = ['insured', 'patient', 'claim-details', 'provider', 'payment', 'consent'];

export function ClaimInfoCard({ claim, claimApplicationData }: IClaimInfoCardProps) {
  if (!claimApplicationData) {
    return (
      <div>
        <h2 className="text-base font-semibold text-foreground mb-4">Claim Information</h2>
        <div className="border border-border rounded-lg bg-card p-6">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 text-sm">
            <div>
              <dt className="text-muted-foreground">Claimant Type</dt>
              <dd className="font-medium mt-0.5 capitalize">{claim.claimantType}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Claimant ID</dt>
              <dd className="font-medium mt-0.5">{claim.claimantId}</dd>
            </div>
            {claim.treatmentType && (
              <div>
                <dt className="text-muted-foreground">Treatment Type</dt>
                <dd className="font-medium mt-0.5">{claim.treatmentType}</dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground">Date of Service</dt>
              <dd className="font-medium mt-0.5">{formatDate(claim.dateOfService)}</dd>
            </div>
            {claim.dateOfInjuryOrIllness && (
              <div>
                <dt className="text-muted-foreground">Date of Injury/Illness</dt>
                <dd className="font-medium mt-0.5">{formatDate(claim.dateOfInjuryOrIllness)}</dd>
              </div>
            )}
            {claim.diagnosis && (
              <div>
                <dt className="text-muted-foreground">Diagnosis</dt>
                <dd className="font-medium mt-0.5">{claim.diagnosis}</dd>
              </div>
            )}
            {claim.symptoms && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-muted-foreground">Symptoms</dt>
                <dd className="font-medium mt-0.5">{claim.symptoms}</dd>
              </div>
            )}
            {claim.providerName && (
              <div>
                <dt className="text-muted-foreground">Provider</dt>
                <dd className="font-medium mt-0.5">{claim.providerName}</dd>
              </div>
            )}
            {claim.providerPhone && (
              <div>
                <dt className="text-muted-foreground">Provider Phone</dt>
                <dd className="font-medium mt-0.5">{claim.providerPhone}</dd>
              </div>
            )}
            {claim.providerEmail && (
              <div>
                <dt className="text-muted-foreground">Provider Email</dt>
                <dd className="font-medium mt-0.5">{claim.providerEmail}</dd>
              </div>
            )}
            {claim.providerAddress && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-muted-foreground">Provider Address</dt>
                <dd className="font-medium mt-0.5">{claim.providerAddress}</dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground">Total Claimed</dt>
              <dd className="font-medium mt-0.5">
                {formatMoney(claim.totalAmountClaimed, claim.currency)}
              </dd>
            </div>
            {claim.reimburseTarget && (
              <div>
                <dt className="text-muted-foreground">Reimburse To</dt>
                <dd className="font-medium mt-0.5 capitalize">{claim.reimburseTarget}</dd>
              </div>
            )}
            {claim.bankName && (
              <div>
                <dt className="text-muted-foreground">Bank</dt>
                <dd className="font-medium mt-0.5">{claim.bankName}</dd>
              </div>
            )}
            {claim.bankAccountHolder && (
              <div>
                <dt className="text-muted-foreground">Account Holder</dt>
                <dd className="font-medium mt-0.5">{claim.bankAccountHolder}</dd>
              </div>
            )}
            {claim.bankIban && (
              <div>
                <dt className="text-muted-foreground">IBAN</dt>
                <dd className="font-medium mt-0.5 font-mono text-xs">{claim.bankIban}</dd>
              </div>
            )}
            {claim.bankSwift && (
              <div>
                <dt className="text-muted-foreground">SWIFT</dt>
                <dd className="font-medium mt-0.5 font-mono text-xs">{claim.bankSwift}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    );
  }

  const v = claimApplicationData.values;

  return (
    <div>
      <h2 className="text-base font-semibold text-foreground mb-4">Claim Information</h2>
      <Accordion type="multiple" defaultValue={ALL_SECTIONS} className="space-y-3">
        <AccordionItem value="insured" className="border border-border rounded-lg overflow-hidden">
          <AccordionTrigger className="text-sm font-medium gap-2 bg-muted/50 px-4 hover:bg-muted cursor-pointer">
            <User className="h-4 w-4 shrink-0 text-muted-foreground" />
            Insured
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-3">
            <SectionGrid>
              <InfoField label="Last Name" value={str(v['insured_lastName'])} />
              <InfoField label="First Name" value={str(v['insured_firstName'])} />
              <InfoField label="Date of Birth" value={str(v['insured_dateOfBirth'])} />
              <InfoField label="Employer Name" value={str(v['insured_employerName'])} />
              <InfoField label="Policy Number" value={str(v['insured_policyNumber'])} />
              <InfoField label="Member ID" value={str(v['insured_memberIdNumber'])} />
              <InfoField label="Email" value={str(v['insured_email'])} />
              <InfoField label="Phone" value={str(v['insured_phone'])} />
              <InfoField label="Address" value={formatAddress(v['insured_address'])} />
            </SectionGrid>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="patient" className="border border-border rounded-lg overflow-hidden">
          <AccordionTrigger className="text-sm font-medium gap-2 bg-muted/50 px-4 hover:bg-muted cursor-pointer">
            <Heart className="h-4 w-4 shrink-0 text-muted-foreground" />
            Patient
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-3">
            <SectionGrid>
              <InfoField label="Last Name" value={str(v['patient_lastName'])} />
              <InfoField label="First Name" value={str(v['patient_firstName'])} />
              <InfoField label="Date of Birth" value={str(v['patient_dateOfBirth'])} />
              <InfoField label="Member ID" value={str(v['patient_memberIdNumber'])} />
              <InfoField label="Relationship" value={str(v['patient_relationship'])} />
              <InfoField label="Phone" value={str(v['patient_phone'])} />
              <InfoField label="Address" value={formatAddress(v['patient_address'])} />
              <InfoField
                label="Previously Covered"
                value={formatConditionalDetail(v['patient_previouslyCovered'])}
              />
            </SectionGrid>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="claim-details"
          className="border border-border rounded-lg overflow-hidden"
        >
          <AccordionTrigger className="text-sm font-medium gap-2 bg-muted/50 px-4 hover:bg-muted cursor-pointer">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            Claim Details
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-3">
            <SectionGrid>
              <InfoField label="Date of Illness" value={str(v['claim_dateOfIllness'])} />
              <InfoField label="Treatment Type" value={str(v['claim_treatmentType'])} />
              <InfoField label="Symptoms" value={str(v['claim_symptoms'])} />
              <InfoField label="Diagnosis" value={str(v['claim_diagnosis'])} />
              <InfoField
                label="Previous Treatment"
                value={formatConditionalDetail(v['claim_previousTreatment'])}
              />
              <InfoField
                label="Related to Occupation"
                value={formatConditionalDetail(v['claim_relatedToOccupation'])}
              />
              <InfoField
                label="Related to Accident"
                value={formatConditionalDetail(v['claim_relatedToAccident'])}
              />
              <InfoField
                label="Maternity Treatment"
                value={formatConditionalDetail(v['claim_maternityTreatment'])}
              />
              <InfoField label="Additional Remarks" value={str(v['claim_additionalRemarks'])} />
            </SectionGrid>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="provider" className="border border-border rounded-lg overflow-hidden">
          <AccordionTrigger className="text-sm font-medium gap-2 bg-muted/50 px-4 hover:bg-muted cursor-pointer">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            Provider &amp; Treatment
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-3">
            <SectionGrid>
              <InfoField label="Provider Name" value={str(v['provider_name'])} />
              <InfoField label="Phone" value={str(v['provider_phone'])} />
              <InfoField label="Email" value={str(v['provider_email'])} />
              <InfoField label="Address" value={formatAddress(v['provider_address'])} />
            </SectionGrid>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="payment" className="border border-border rounded-lg overflow-hidden">
          <AccordionTrigger className="text-sm font-medium gap-2 bg-muted/50 px-4 hover:bg-muted cursor-pointer">
            <CreditCard className="h-4 w-4 shrink-0 text-muted-foreground" />
            Payment
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-3">
            <SectionGrid>
              <InfoField label="Reimburse To" value={str(v['payment_reimburseTo'])} />
              <InfoField label="Bank Name" value={str(v['bank_name'])} />
              <InfoField label="Account Holder" value={str(v['bank_accountHolder'])} />
              <InfoField label="IBAN" value={str(v['bank_iban'])} />
              <InfoField label="SWIFT" value={str(v['bank_swift'])} />
              <InfoField label="Routing" value={str(v['bank_routing'])} />
              <InfoField label="Bank Address" value={formatAddress(v['bank_address'])} />
            </SectionGrid>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="consent" className="border border-border rounded-lg overflow-hidden">
          <AccordionTrigger className="text-sm font-medium gap-2 bg-muted/50 px-4 hover:bg-muted cursor-pointer">
            <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
            Consent
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-3">
            <SectionGrid>
              <InfoField
                label="Data Processing"
                value={
                  v['consent_dataProcessing'] === true
                    ? 'Agreed'
                    : v['consent_dataProcessing'] === false
                      ? 'Not agreed'
                      : '—'
                }
              />
              <InfoField
                label="Privacy Policy"
                value={
                  v['consent_privacyPolicy'] === true
                    ? 'Agreed'
                    : v['consent_privacyPolicy'] === false
                      ? 'Not agreed'
                      : '—'
                }
              />
              <InfoField label="Signature Name" value={str(v['signature_fullName'])} />
              <InfoField label="Signature Date" value={str(v['signature_date'])} />
            </SectionGrid>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
